import React, { useState } from 'react'
import { Outlet, useNavigate, useLocation } from 'react-router-dom'
import { useDispatch, useSelector } from 'react-redux'
import {
  Box,
  Drawer,
  IconButton,
  List,
  ListItem,
  ListItemButton,
  ListItemIcon,
  Menu,
  MenuItem,
  Avatar,
  Divider,
  useTheme,
  useMediaQuery,
  alpha,
  Tooltip,
} from '@mui/material'
import {
  Menu as MenuIcon,
  Dashboard,
  School,
  Assessment,
  AdminPanelSettings,
  AccountCircle,
  Logout,
  Group,
  Analytics,
  Settings,
} from '@mui/icons-material'

import { RootState, AppDispatch } from '../app/store'
import { clearAuth } from '../features/auth/authSlice'
import { useSSOLogout } from '../context/MsalProvider'
import { isAzureSSOConfigured } from '../config/authConfig'

const drawerWidth = 80 // Thin sidebar with icons only

const ModernLayout: React.FC = () => {
  const theme = useTheme()
  const isMobile = useMediaQuery(theme.breakpoints.down('md'))
  const navigate = useNavigate()
  const location = useLocation()
  const dispatch = useDispatch<AppDispatch>()
  const { user } = useSelector((state: RootState) => state.auth)
  const { logout: ssoLogout, isConfigured: ssoConfigured } = useSSOLogout()

  const [mobileOpen, setMobileOpen] = useState(false)
  const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null)

  const handleDrawerToggle = () => {
    setMobileOpen(!mobileOpen)
  }

  const handleProfileMenuOpen = (event: React.MouseEvent<HTMLElement>) => {
    setAnchorEl(event.currentTarget)
  }

  const handleProfileMenuClose = () => {
    setAnchorEl(null)
  }

  const handleLogout = async () => {
    handleProfileMenuClose()
    
    if (isAzureSSOConfigured() && ssoConfigured) {
      // Use SSO logout - will redirect to Azure AD
      await ssoLogout()
    } else {
      // Fallback to legacy logout
      dispatch(clearAuth())
      navigate('/login')
    }
  }

  // Helper to check if user has any of the required permissions
  const hasAnyPermission = (permissions?: string[]) => {
    if (!permissions || permissions.length === 0) return true // No restrictions
    if (!user?.permissions) return false
    return permissions.some(perm => user.permissions.includes(perm))
  }

  const menuItems = [
    { 
      text: 'Dashboard', 
      icon: <Dashboard />, 
      path: '/dashboard', 
      permissions: [] // Available to all authenticated users
    },
    { 
      text: 'Courses', 
      icon: <School />, 
      path: '/courses', 
      permissions: [] // Available to all authenticated users
    },
    { 
      text: 'Assessments', 
      icon: <Assessment />, 
      path: '/assessments', 
      permissions: [] // Available to all authenticated users
    },
  ]

  const adminMenuItems = [
    { 
      text: 'Admin Dashboard', 
      icon: <AdminPanelSettings />, 
      path: '/admin', 
      permissions: ['courses.add_course', 'courses.change_course', 'assessments.add_assessment', 'assessments.change_assessment'] // Any management permission
    },
    { 
      text: 'User Management', 
      icon: <Group />, 
      path: '/admin/users', 
      permissions: ['users.add_user', 'users.change_user'] // User management permissions
    },
    { 
      text: 'Course Management', 
      icon: <School />, 
      path: '/admin/courses', 
      permissions: ['courses.add_course', 'courses.change_course'] // Course management permissions
    },
    { 
      text: 'Assessment Management', 
      icon: <Assessment />, 
      path: '/admin/assessments', 
      permissions: ['assessments.add_assessment', 'assessments.change_assessment'] // Assessment management permissions
    },
    { 
      text: 'Reports', 
      icon: <Analytics />, 
      path: '/admin/reports', 
      permissions: ['courses.view_course', 'assessments.view_assessment'] // View permissions for reports
    },
  ]

  // Filter menu items based on permissions
  const filteredMenuItems = user 
    ? menuItems.filter(item => hasAnyPermission(item.permissions))
    : menuItems // Show all basic menu items if no user (will be protected by route)

  const filteredAdminMenuItems = user
    ? adminMenuItems.filter(item => hasAnyPermission(item.permissions))
    : [] // Hide admin items if no user

  const isItemActive = (path: string) => location.pathname === path

  const SidebarHeader = () => (
    <Box sx={{ p: 2, textAlign: 'center' }}>
      <Box
        sx={{
          width: 48,
          height: 48,
          borderRadius: '16px',
          background: 'linear-gradient(135deg, #1e293b 0%, #334155 100%)', // Dark slate background for orange logo
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          mx: 'auto',
          overflow: 'hidden',
          cursor: 'pointer',
        }}
        onClick={() => navigate('/dashboard')}
      >
        <img
          src="/logo.png"
          alt="Devon Logo"
          style={{
            width: '32px',
            height: '32px',
            objectFit: 'contain',
          }}
        />
      </Box>
    </Box>
  )

  const ModernListItem = ({ item }: { item: any }) => (
    <ListItem key={item.text} sx={{ px: 1, py: 0.5, justifyContent: 'center' }}>
      <Tooltip title={item.text} placement="right" arrow>
        <ListItemButton
          onClick={() => navigate(item.path)}
          selected={isItemActive(item.path)}
          sx={{
            borderRadius: '12px',
            minWidth: 'unset',
            width: 48,
            height: 48,
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            mb: 1,
            transition: 'all 0.2s cubic-bezier(0.4, 0, 0.2, 1)',
            '&:hover': {
              bgcolor: alpha(theme.palette.primary.main, 0.08),
              transform: 'scale(1.05)',
            },
            '&.Mui-selected': {
              bgcolor: alpha(theme.palette.primary.main, 0.12),
              color: theme.palette.primary.main,
              '&:hover': {
                bgcolor: alpha(theme.palette.primary.main, 0.16),
              },
            },
          }}
        >
          <Box
            sx={{
              color: isItemActive(item.path) ? theme.palette.primary.main : 'text.secondary',
              transition: 'color 0.2s ease',
            }}
          >
            {item.icon}
          </Box>
        </ListItemButton>
      </Tooltip>
    </ListItem>
  )

  const drawer = (
    <Box sx={{ height: '100%', display: 'flex', flexDirection: 'column' }}>
      <SidebarHeader />
      
      <Divider sx={{ mx: 1, mb: 2 }} />
      
      <Box sx={{ flex: 1, overflowY: 'auto' }}>
        <List sx={{ px: 0 }}>
          {filteredMenuItems.map((item) => (
            <ModernListItem key={item.text} item={item} />
          ))}
        </List>
        
        {filteredAdminMenuItems.length > 0 && (
          <>
            <Divider sx={{ mx: 1, my: 2 }} />
            <List sx={{ px: 0 }}>
              {filteredAdminMenuItems.map((item) => (
                <ModernListItem key={item.text} item={item} />
              ))}
            </List>
          </>
        )}
      </Box>
      
      {/* User Profile Icon at bottom */}
      <Box sx={{ p: 1 }}>
        <Tooltip title={`${user?.first_name} ${user?.last_name}`} placement="right" arrow>
          <IconButton
            onClick={handleProfileMenuOpen}
            sx={{
              width: 48,
              height: 48,
              borderRadius: '12px',
              bgcolor: alpha(theme.palette.primary.main, 0.08),
              '&:hover': {
                bgcolor: alpha(theme.palette.primary.main, 0.12),
                transform: 'scale(1.05)',
              },
            }}
          >
            <Avatar 
              sx={{ 
                width: 32, 
                height: 32,
                bgcolor: theme.palette.primary.main,
                fontSize: '0.875rem',
                fontWeight: 600,
              }}
            >
              {user?.first_name?.charAt(0) || 'U'}
            </Avatar>
          </IconButton>
        </Tooltip>
      </Box>
    </Box>
  )

  return (
    <Box sx={{ display: 'flex', minHeight: '100vh' }}>
      {/* Thin Icon Sidebar */}
      <Box
        component="nav"
        sx={{ width: { md: drawerWidth }, flexShrink: { md: 0 } }}
      >
        <Drawer
          variant={isMobile ? 'temporary' : 'permanent'}
          open={isMobile ? mobileOpen : true}
          onClose={handleDrawerToggle}
          ModalProps={{ keepMounted: true }}
          sx={{
            '& .MuiDrawer-paper': {
              boxSizing: 'border-box',
              width: drawerWidth,
              borderRight: 1,
              borderColor: 'divider',
              bgcolor: 'background.paper',
              boxShadow: '0 4px 20px rgba(0,0,0,0.08)',
            },
          }}
        >
          {drawer}
        </Drawer>
      </Box>

      {/* Main Content - Full width without header */}
      <Box
        component="main"
        sx={{
          flexGrow: 1,
          width: { md: `calc(100% - ${drawerWidth}px)` },
          minHeight: '100vh',
          bgcolor: 'background.default',
        }}
      >
        {/* Mobile menu button overlay when needed */}
        {isMobile && (
          <Box
            sx={{
              position: 'fixed',
              top: 16,
              left: 16,
              zIndex: theme.zIndex.appBar,
            }}
          >
            <IconButton
              color="primary"
              aria-label="open drawer"
              onClick={handleDrawerToggle}
              sx={{
                bgcolor: 'background.paper',
                boxShadow: '0 2px 8px rgba(0,0,0,0.15)',
                '&:hover': {
                  bgcolor: 'background.paper',
                  boxShadow: '0 4px 12px rgba(0,0,0,0.2)',
                },
              }}
            >
              <MenuIcon />
            </IconButton>
          </Box>
        )}
        
        <Box sx={{ p: 0 }}>
          <Outlet />
        </Box>
      </Box>

      {/* Profile Menu */}
      <Menu
        anchorEl={anchorEl}
        open={Boolean(anchorEl)}
        onClose={handleProfileMenuClose}
        PaperProps={{
          sx: {
            borderRadius: '16px',
            minWidth: 200,
            mt: 1,
            boxShadow: '0 8px 32px rgba(0,0,0,0.12)',
          },
        }}
      >
        <MenuItem 
          onClick={() => {
            navigate('/profile')
            handleProfileMenuClose()
          }} 
          sx={{ borderRadius: '12px', mx: 1, my: 0.5 }}
        >
          <ListItemIcon>
            <AccountCircle fontSize="small" />
          </ListItemIcon>
          Profile
        </MenuItem>
        <MenuItem onClick={handleProfileMenuClose} sx={{ borderRadius: '12px', mx: 1, my: 0.5 }}>
          <ListItemIcon>
            <Settings fontSize="small" />
          </ListItemIcon>
          Settings
        </MenuItem>
        <Divider sx={{ my: 1 }} />
        <MenuItem 
          onClick={handleLogout} 
          sx={{ 
            borderRadius: '12px', 
            mx: 1, 
            my: 0.5,
            color: theme.palette.error.main,
          }}
        >
          <ListItemIcon>
            <Logout fontSize="small" sx={{ color: theme.palette.error.main }} />
          </ListItemIcon>
          Logout
        </MenuItem>
      </Menu>
    </Box>
  )
}

export default ModernLayout